import { isSourceBackedDocument } from './document-kind'

interface ReadableDocument {
  title: string
  content?: string | null
  contentJson?: unknown
  sourceProvenance?: unknown | null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function serializeInlineContent(value: unknown): string {
  if (typeof value === 'string') return value
  if (!Array.isArray(value)) return ''

  return value
    .map((item) => {
      if (typeof item === 'string') return item
      const record = asRecord(item)
      if (!record) return ''

      if (record.type === 'link') {
        const label = serializeInlineContent(record.content)
        const href = typeof record.href === 'string' ? record.href.replaceAll(')', '\\)') : ''
        return href ? `[${label}](${href})` : label
      }

      let text =
        typeof record.text === 'string' ? record.text : serializeInlineContent(record.content)
      const styles = asRecord(record.styles)
      if (!text || !styles) return text
      if (styles.code) text = `\`${text.replaceAll('`', '\\`')}\``
      if (styles.bold) text = `**${text}**`
      if (styles.italic) text = `_${text}_`
      if (styles.strike) text = `~~${text}~~`
      return text
    })
    .join('')
}

function serializeTable(value: unknown): string {
  const table = asRecord(value)
  if (!table || !Array.isArray(table.rows)) return ''
  const rows = table.rows
    .map((row) => {
      const record = asRecord(row)
      if (!record || !Array.isArray(record.cells)) return []
      return record.cells.map((cell) => serializeInlineContent(cell).replaceAll('|', '\\|'))
    })
    .filter((row) => row.length > 0)
  if (rows.length === 0) return ''

  const width = Math.max(...rows.map((row) => row.length))
  const normalizedRows = rows.map((row) => [...row, ...Array(width - row.length).fill('')])
  const rowMarkdown = (row: string[]) => `| ${row.join(' | ')} |`
  return [
    rowMarkdown(normalizedRows[0]),
    rowMarkdown(Array.from({ length: width }, () => '---')),
    ...normalizedRows.slice(1).map(rowMarkdown),
  ].join('\n')
}

function serializeBlock(value: unknown, index: number): string {
  const block = asRecord(value)
  if (!block) return ''

  const text = serializeInlineContent(block.content)
  const props = asRecord(block.props)
  let result = text

  switch (block.type) {
    case 'heading': {
      const rawLevel = Number(props?.level ?? 1)
      const level = Math.min(3, Math.max(1, Number.isFinite(rawLevel) ? rawLevel : 1))
      result = `${'#'.repeat(level)} ${text}`
      break
    }
    case 'bulletListItem':
      result = `- ${text}`
      break
    case 'numberedListItem':
      result = `${index + 1}. ${text}`
      break
    case 'checkListItem':
      result = `- [${props?.checked ? 'x' : ' '}] ${text}`
      break
    case 'blockquote':
      result = text
        .split('\n')
        .map((line) => `> ${line}`)
        .join('\n')
      break
    case 'codeBlock': {
      const language = typeof props?.language === 'string' ? props.language : ''
      result = `\`\`\`${language}\n${text}\n\`\`\``
      break
    }
    case 'image': {
      const url = typeof props?.url === 'string' ? props.url : ''
      const caption = typeof props?.caption === 'string' ? props.caption : ''
      result = url ? `![${caption}](${url})` : caption
      break
    }
    case 'table':
      result = serializeTable(block.content)
      break
  }

  const children = Array.isArray(block.children)
    ? block.children.map((child, childIndex) => serializeBlock(child, childIndex)).filter(Boolean)
    : []
  return [result, ...children].filter(Boolean).join('\n')
}

function serializeContentJson(contentJson: unknown): string {
  if (!Array.isArray(contentJson)) return ''
  return contentJson
    .map((block, index) => serializeBlock(block, index))
    .filter(Boolean)
    .join('\n\n')
}

export function documentReadableContent(doc: ReadableDocument): string {
  if (isSourceBackedDocument(doc)) return doc.content?.trim() ?? ''
  return serializeContentJson(doc.contentJson).trim() || doc.content?.trim() || ''
}

export function normalizeReaderMarkdown(markdown: string, title: string): string {
  const normalizedTitle = title.trim().toLocaleLowerCase()
  let removedMatchingTitle = false

  return markdown
    .split('\n')
    .filter((line) => {
      if (removedMatchingTitle || !line.startsWith('# ')) return true
      if (line.slice(2).trim().toLocaleLowerCase() !== normalizedTitle) return true
      removedMatchingTitle = true
      return false
    })
    .map((line) => (line.startsWith('# ') ? `## ${line.slice(2)}` : line))
    .join('\n')
    .trim()
}
